import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { getAdminUsers, getTopDepositors, getSubscriptionPurchases, updateUserSubscriptionStatus } from "../services/telecoApi";
  import {
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    CardTitle,
    CardFooter,
    Table,
    Badge,
    Button,
    FormGroup,
    Label,
    Input,
    Pagination,
    PaginationItem,
    PaginationLink,
  } from "reactstrap";
import { Bar, Line } from "react-chartjs-2";
import { chartExample3, chartExample2 } from "variables/charts";
import moment from "moment";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

export default function AdminPanel() {
  const history = useHistory();
  const role = localStorage.getItem("role");

  useEffect(() => {
    if (role !== "ROLE_ADMIN") {
      history.push("/admin/history-aviator");
    }
  }, [role, history]);

  // Usuários
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersMeta, setUsersMeta] = useState({ total: 0, page: 1, totalPages: 1 });
  const [userFilters, setUserFilters] = useState({
    search: "",
    role: "",
    active: "",
    created_start: "",
    created_end: "",
    page: 1,
    limit: 20,
    order_by: "created_at",
    order_dir: "DESC",
  });

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const data = await getAdminUsers(userFilters);
      setUsers(data.users || []);
      setUsersMeta({ total: data.total || 0, page: data.page || 1, totalPages: data.totalPages || 1 });
    } catch (err) {
      console.error("Erro ao filtrar usuários:", err);
      await MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Falha ao carregar usuários",
        text: err?.response?.data?.message || err.message || "Erro desconhecido",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const changeUserPage = async (nextPage) => {
    const page = Math.max(1, Math.min(nextPage, usersMeta.totalPages || 1));
    setUserFilters(prev => ({ ...prev, page }));
    const data = await getAdminUsers({ ...userFilters, page });
    setUsers(data.users || []);
    setUsersMeta({ total: data.total || 0, page: data.page || 1, totalPages: data.totalPages || 1 });
  };

  const changeUserLimit = async (limit) => {
    const newLimit = Number(limit) || 20;
    setUserFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));
    const data = await getAdminUsers({ ...userFilters, limit: newLimit, page: 1 });
    setUsers(data.users || []);
    setUsersMeta({ total: data.total || 0, page: data.page || 1, totalPages: data.totalPages || 1 });
  };

  // Ranking Depósitos
  const [deposits, setDeposits] = useState([]);
  const [depositFilters, setDepositFilters] = useState({ month: "", start: "", end: "", limit: 10, statuses: "PAID,APPROVED" });
  const [loadingDeposits, setLoadingDeposits] = useState(false);
  const loadDeposits = async () => {
    try {
      setLoadingDeposits(true);
      const data = await getTopDepositors(depositFilters);
      setDeposits(data?.data || []);
    } catch (err) {
      console.error("Erro ao filtrar depósitos:", err);
      await MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Falha ao carregar ranking",
        text: err?.response?.data?.message || err.message || "Erro desconhecido",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoadingDeposits(false);
    }
  };

  // Assinaturas
  const [subs, setSubs] = useState([]);
  const [subsFilters, setSubsFilters] = useState({ start: "", end: "", status: "active", plan: "", limit: 100 });
  const [subsPage, setSubsPage] = useState(1);
  const [subsLimit, setSubsLimit] = useState(10);
  const [loadingSubs, setLoadingSubs] = useState(false);
  const loadSubs = async () => {
    try {
      setLoadingSubs(true);
      const data = await getSubscriptionPurchases(subsFilters);
      setSubs(data?.data || []);
    } catch (err) {
      console.error("Erro ao filtrar assinaturas:", err);
      await MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Falha ao carregar assinaturas",
        text: err?.response?.data?.message || err.message || "Erro desconhecido",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoadingSubs(false);
    }
  };

  // ======== Formatações ========
  const formatCPF = (v) => {
    const s = String(v || "").replace(/\D/g, "");
    if (s.length !== 11) return v || "-";
    return `${s.slice(0,3)}.${s.slice(3,6)}.${s.slice(6,9)}-${s.slice(9)}`;
  };

  const formatPhone = (v) => {
    const s = String(v || "").replace(/\D/g, "");
    if (s.length === 11) {
      return `(${s.slice(0,2)}) ${s.slice(2,7)}-${s.slice(7)}`; // celular
    }
    if (s.length === 10) {
      return `(${s.slice(0,2)}) ${s.slice(2,6)}-${s.slice(6)}`; // fixo
    }
    return v || "-";
  };

  const formatDate = (v) => {
    const m = moment(v);
    return m.isValid() ? m.format("DD/MM/YYYY") : (v || "-");
  };

  const formatDateTime = (v) => {
    const m = moment(v);
    return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : (v || "-");
  };

  useEffect(() => {
    loadUsers();
    loadDeposits();
    loadSubs();
  }, []);

  // ======== Métricas agregadas ========
  const totalDepositAmount = useMemo(
    () => deposits.reduce((sum, d) => sum + Number(d.total_amount || 0), 0),
    [deposits]
  );
  const topDepositor = deposits[0] || null;
  const activeSubsCount = useMemo(
    () => subs.filter((s) => String(s.subscription_status).toLowerCase() === "active").length,
    [subs]
  );
  const totalSubsCount = subs.length;
  const totalUsers = usersMeta.total || 0;

  // Toggle de plano do usuário
  const toggleUserPlan = async (user) => {
    const current = String(user.subscription_status || "").toLowerCase();
    const next = current === "active" ? "canceled" : "active";

    try {
      // Confirmação inicial
      const confirm = await MySwal.fire({
        title: next === "active" ? "Ativar plano?" : "Desativar plano?",
        text: next === "active" ?
          `Você está prestes a ativar o plano para ${user.name}.` :
          `Você está prestes a desativar o plano de ${user.name}.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: next === "active" ? "Ativar" : "Desativar",
        cancelButtonText: "Cancelar",
      });
      if (!confirm.isConfirmed) return;

      let days = undefined;
      if (next === "active") {
        const input = await MySwal.fire({
          title: "Por quantos dias o plano ficará ativo?",
          input: "number",
          inputAttributes: { min: 1, step: 1 },
          inputPlaceholder: "Ex.: 30",
          icon: "info",
          showCancelButton: true,
          confirmButtonText: "Confirmar",
          cancelButtonText: "Cancelar",
          preConfirm: (val) => {
            const n = Number(val);
            if (!Number.isFinite(n) || n <= 0) {
              MySwal.showValidationMessage("Informe um número válido (>= 1)");
            }
            return n;
          },
        });
        if (!input.isConfirmed) return;
        days = Number(input.value);
      }

      const { user: updatedUser } = await updateUserSubscriptionStatus(user.id, next, { days });

      // Atualiza UI localmente
      setUsers(prev => prev.map(u => (
        u.id === user.id ? { ...u, subscription_status: next, subscription_ends_at: updatedUser?.subscription_ends_at || u.subscription_ends_at } : u
      )));

      await MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: next === "active" ? "Plano ativado com sucesso" : "Plano desativado com sucesso",
        showConfirmButton: false,
        timer: 2500,
      });
    } catch (e) {
      console.error("Falha ao atualizar plano:", e);
      await MySwal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Falha ao atualizar o plano",
        text: e?.response?.data?.message || e.message || "Erro desconhecido",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  // ======== Dados dos gráficos ========
  const depositsBarData = useMemo(() => {
    const labels = deposits.map((d) => d.name || d.email || d.cpf || "-");
    const dataset = deposits.map((d) => Number(d.total_amount || 0));

    return {
      labels,
      datasets: [
        {
          label: "Total por usuário (R$)",
          fill: true,
          backgroundColor: "rgba(72,72,176,0.1)",
          borderColor: "#d048b6",
          borderWidth: 2,
          data: dataset,
        },
      ],
    };
  }, [deposits]);

  const subsLineData = useMemo(() => {
    // Agrupa por dia do último pagamento
    const byDay = new Map();
    subs.forEach((s) => {
      const d = s.last_payment_at ? new Date(s.last_payment_at) : null;
      if (!d || Number.isNaN(d.getTime())) return;
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
      byDay.set(key, (byDay.get(key) || 0) + 1);
    });
    const labels = Array.from(byDay.keys()).sort();
    const values = labels.map((k) => byDay.get(k));
    return {
      labels,
      datasets: [
        {
          label: "Compras/assinaturas por dia",
          fill: true,
          backgroundColor: "rgba(29,140,248,0.2)",
          borderColor: "#1f8ef1",
          borderWidth: 2,
          data: values,
        },
      ],
    };
  }, [subs]);

  return (
    <div className="content">
      <h3>Admin Panel</h3>

      {/* Cards de estatísticas rápidas */}
      <Row>
        <Col lg="3" md="6" sm="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col md="8" xs="7">
                  <CardTitle tag="h5">Total Depósitos</CardTitle>
                  <h3 className="card-title">R$ {totalDepositAmount.toFixed(2)}</h3>
                </Col>
                <Col md="4" xs="5" className="text-right">
                  <i className="tim-icons icon-bag-16 text-success" />
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <div className="stats">
                {topDepositor ? (
                  <span>
                    Top: {topDepositor.name || topDepositor.email || topDepositor.cpf} — R$ {Number(topDepositor.total_amount).toFixed(2)}
                  </span>
                ) : (
                  <span>Sem dados no período</span>
                )}
              </div>
            </CardFooter>
          </Card>
        </Col>

        <Col lg="3" md="6" sm="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col md="8" xs="7">
                  <CardTitle tag="h5">Assinaturas ativas</CardTitle>
                  <h3 className="card-title">
                    {activeSubsCount} <Badge color="success" pill>de {totalSubsCount}</Badge>
                  </h3>
                </Col>
                <Col md="4" xs="5" className="text-right">
                  <i className="tim-icons icon-refresh-01 text-info" />
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <div className="stats">Últ. atualização: agora</div>
            </CardFooter>
          </Card>
        </Col>

        <Col lg="3" md="6" sm="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col md="8" xs="7">
                  <CardTitle tag="h5">Usuários</CardTitle>
                  <h3 className="card-title">{totalUsers}</h3>
                </Col>
                <Col md="4" xs="5" className="text-right">
                  <i className="tim-icons icon-single-02 text-primary" />
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <div className="stats">Total cadastrado</div>
            </CardFooter>
          </Card>
        </Col>

        <Col lg="3" md="6" sm="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col md="8" xs="7">
                  <CardTitle tag="h5">Depositores</CardTitle>
                  <h3 className="card-title">{deposits.length}</h3>
                </Col>
                <Col md="4" xs="5" className="text-right">
                  <i className="tim-icons icon-chart-bar-32 text-warning" />
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <div className="stats">No ranking atual</div>
            </CardFooter>
          </Card>
        </Col>
      </Row>

      {/* Usuários */}
      <Card className="mt-3">
        <CardHeader>
          <CardTitle tag="h4">Usuários</CardTitle>
        </CardHeader>
        <CardBody>
          <Row className="mb-3">
            <Col md="3">
              <FormGroup>
                <Label>Busca</Label>
                <Input placeholder="nome/email/CPF" value={userFilters.search} onChange={e => setUserFilters(prev => ({ ...prev, search: e.target.value }))} />
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label>Role</Label>
                <Input type="select" value={userFilters.role} onChange={e => setUserFilters(prev => ({ ...prev, role: e.target.value }))}>
                  <option value="">Todos</option>
                  <option value="user">user</option>
                  <option value="ROLE_ADMIN">ROLE_ADMIN</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup>
                <Label>Ativo</Label>
                <Input type="select" value={userFilters.active} onChange={e => setUserFilters(prev => ({ ...prev, active: e.target.value }))}>
                  <option value="">Todos</option>
                  <option value="true">true</option>
                  <option value="false">false</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup>
                <Label>Início</Label>
                <Input type="date" value={userFilters.created_start} onChange={e => setUserFilters(prev => ({ ...prev, created_start: e.target.value }))} />
              </FormGroup>
            </Col>
            <Col md="2">
              <FormGroup>
                <Label>Fim</Label>
                <Input type="date" value={userFilters.created_end} onChange={e => setUserFilters(prev => ({ ...prev, created_end: e.target.value }))} />
              </FormGroup>
            </Col>
            <Col md="12" className="text-right">
              <Button color="primary" onClick={loadUsers} disabled={loadingUsers}>Filtrar</Button>
            </Col>
          </Row>

          <Table responsive>
            <thead className="text-primary">
              <tr>
                <th>Nome</th>
                <th>Email</th>
                <th>Telefone</th>
                <th>CPF</th>
                <th>Role</th>
                <th>Plano</th>
                <th>Expira</th>
                <th>Ativo</th>
                <th>Criado</th>
                <th>Editar</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>{u.name}</td>
                  <td>{u.email}</td>
                  <td>{formatPhone(u.phone)}</td>
                  <td>{formatCPF(u.cpf)}</td>
                  <td>
                    <Badge color={u.role === "ROLE_ADMIN" ? "warning" : "secondary"}>{u.role}</Badge>
                  </td>
                  <td>
                    <Badge color={String(u.subscription_status).toLowerCase() === "active" ? "success" : "warning"}>{u.subscription_status || "-"}</Badge>
                  </td>
                  <td>{formatDate(u.subscription_ends_at)}</td>
                  <td>
                    <Badge color={u.active ? "success" : "danger"}>{String(u.active)}</Badge>
                  </td>
                  <td>{formatDateTime(u.created_at)}</td>
                  <td>
                    <Button size="sm" color={String(u.subscription_status).toLowerCase() === "active" ? "danger" : "success"}
                      onClick={() => toggleUserPlan(u)}>
                      {String(u.subscription_status).toLowerCase() === "active" ? "Desativar plano" : "Ativar plano"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Row className="align-items-center">
            <Col md="6" className="mb-2">
              <span>Paginação: {usersMeta.page} / {usersMeta.totalPages} (total {usersMeta.total})</span>
            </Col>
            <Col md="3" className="mb-2">
              <FormGroup>
                <Label>Tamanho da página</Label>
                <Input type="select" value={userFilters.limit} onChange={e => changeUserLimit(e.target.value)}>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md="3" className="mb-2">
              <Pagination className="justify-content-end">
                <PaginationItem disabled={usersMeta.page <= 1}>
                  <PaginationLink onClick={() => changeUserPage(1)} first />
                </PaginationItem>
                <PaginationItem disabled={usersMeta.page <= 1}>
                  <PaginationLink onClick={() => changeUserPage(usersMeta.page - 1)} previous />
                </PaginationItem>
                <PaginationItem disabled={usersMeta.page >= usersMeta.totalPages}>
                  <PaginationLink onClick={() => changeUserPage(usersMeta.page + 1)} next />
                </PaginationItem>
                <PaginationItem disabled={usersMeta.page >= usersMeta.totalPages}>
                  <PaginationLink onClick={() => changeUserPage(usersMeta.totalPages)} last />
                </PaginationItem>
              </Pagination>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Ranking de Depósitos */}
      <Row className="mt-4">
        <Col lg="8">
          <Card className="card-chart">
            <CardHeader>
              <h5 className="card-category">Ranking por valor</h5>
              <CardTitle tag="h3">
                <i className="tim-icons icon-chart-bar-32 text-warning" /> Depósitos (R$)
              </CardTitle>
            </CardHeader>
            <CardBody>
              <div className="chart-area">
                <Bar data={depositsBarData} options={chartExample3.options} />
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg="4">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Filtros</CardTitle>
            </CardHeader>
            <CardBody>
              <FormGroup>
                <Label>Mês</Label>
                <Input type="month" value={depositFilters.month} onChange={e => setDepositFilters(prev => ({ ...prev, month: e.target.value }))} />
              </FormGroup>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Início</Label>
                    <Input type="date" value={depositFilters.start} onChange={e => setDepositFilters(prev => ({ ...prev, start: e.target.value }))} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Fim</Label>
                    <Input type="date" value={depositFilters.end} onChange={e => setDepositFilters(prev => ({ ...prev, end: e.target.value }))} />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>Status (CSV)</Label>
                <Input placeholder="PAID,APPROVED" value={depositFilters.statuses} onChange={e => setDepositFilters(prev => ({ ...prev, statuses: e.target.value }))} />
              </FormGroup>
              <Button color="primary" block onClick={loadDeposits} disabled={loadingDeposits}>Filtrar</Button>
            </CardBody>
          </Card>
          <Card className="mt-3">
            <CardHeader>
              <CardTitle tag="h4">Top depositantes</CardTitle>
            </CardHeader>
            <CardBody>
              <Table responsive>
                <thead className="text-primary">
                  <tr>
                    <th>CPF</th>
                    <th>Nome</th>
                    <th>Total (R$)</th>
                    <th>Qtd</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(deposits) && deposits.map((d, idx) => (
                    <tr key={idx}>
                      <td>{d.cpf}</td>
                      <td>{d.name || d.email}</td>
                      <td>{Number(d.total_amount).toFixed(2)}</td>
                      <td>{d.qtd_depositos}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Compras / Assinaturas */}
      <Row className="mt-4">
        <Col lg="8">
          <Card className="card-chart">
            <CardHeader>
              <h5 className="card-category">Compras por dia</h5>
              <CardTitle tag="h3">
                <i className="tim-icons icon-bell-55 text-info" /> Assinaturas
              </CardTitle>
            </CardHeader>
            <CardBody>
              <div className="chart-area">
                <Line data={subsLineData} options={chartExample2.options} />
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg="4">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Filtros</CardTitle>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Início</Label>
                    <Input type="date" value={subsFilters.start} onChange={e => setSubsFilters(prev => ({ ...prev, start: e.target.value }))} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Fim</Label>
                    <Input type="date" value={subsFilters.end} onChange={e => setSubsFilters(prev => ({ ...prev, end: e.target.value }))} />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>Status (CSV)</Label>
                <Input placeholder="active,canceled" value={subsFilters.status} onChange={e => setSubsFilters(prev => ({ ...prev, status: e.target.value }))} />
              </FormGroup>
              <FormGroup>
                <Label>Planos (CSV)</Label>
                <Input placeholder="PREMIUM,BASIC" value={subsFilters.plan} onChange={e => setSubsFilters(prev => ({ ...prev, plan: e.target.value }))} />
              </FormGroup>
              <Button color="primary" block onClick={loadSubs} disabled={loadingSubs}>Filtrar</Button>
            </CardBody>
          </Card>
          {/* Mantém apenas filtros nesta coluna */}
        </Col>
      </Row>

      {/* Assinantes em largura total (grid 12) */}
      <Row className="mt-3">
        <Col lg="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Assinantes</CardTitle>
            </CardHeader>
            <CardBody>
              {/* Paginação client-side das assinaturas */}
              <Table responsive>
                <thead className="text-primary">
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>CPF</th>
                    <th>Status</th>
                    <th>Plano</th>
                    <th>Últ. pagamento</th>
                    <th>Expira</th>
                  </tr>
                </thead>
                <tbody>
                  {subs
                    .slice(((subsPage - 1) * subsLimit), ((subsPage - 1) * subsLimit) + subsLimit)
                    .map((s, idx) => (
                    <tr key={idx}>
                      <td>{s.name}</td>
                      <td>{s.email}</td>
                      <td>{formatCPF(s.cpf)}</td>
                      <td>
                        <Badge color={String(s.subscription_status).toLowerCase() === "active" ? "success" : "warning"}>{s.subscription_status}</Badge>
                      </td>
                      <td>{s.subscription_plan}</td>
                      <td>{formatDateTime(s.last_payment_at)}</td>
                      <td>{formatDate(s.subscription_ends_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Row className="align-items-center">
                <Col md="6" className="mb-2">
                  <span>Paginação: {subsPage} / {Math.max(1, Math.ceil(subs.length / subsLimit))} (total {subs.length})</span>
                </Col>
                <Col md="3" className="mb-2">
                  <FormGroup>
                    <Label>Tamanho da página</Label>
                    <Input type="select" value={subsLimit} onChange={e => { setSubsLimit(Number(e.target.value)); setSubsPage(1); }}>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="3" className="mb-2">
                  <Pagination className="justify-content-end">
                    <PaginationItem disabled={subsPage <= 1}>
                      <PaginationLink onClick={() => setSubsPage(1)} first />
                    </PaginationItem>
                    <PaginationItem disabled={subsPage <= 1}>
                      <PaginationLink onClick={() => setSubsPage(subsPage - 1)} previous />
                    </PaginationItem>
                    <PaginationItem disabled={subsPage >= Math.max(1, Math.ceil(subs.length / subsLimit))}>
                      <PaginationLink onClick={() => setSubsPage(subsPage + 1)} next />
                    </PaginationItem>
                    <PaginationItem disabled={subsPage >= Math.max(1, Math.ceil(subs.length / subsLimit))}>
                      <PaginationLink onClick={() => setSubsPage(Math.max(1, Math.ceil(subs.length / subsLimit)))} last />
                    </PaginationItem>
                  </Pagination>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}